/**
 * What a history entry is, when one is created, and what pressing Back means.
 *
 * The app used to write `replaceState` only, on the argument that "this is about surviving a
 * reload, not about making Back walk the app" — Back always left Nihongonator, from anywhere.
 * 日本-321 reverses that. The hard part is not pushing entries; it is deciding **which acts deserve
 * one**, because an entry per filter chip or per `J` press buries the Back that leaves the site
 * under dozens of states nobody thinks of as places.
 *
 * The rules live here, as functions rather than as prose, so they are testable and so a call site
 * cannot quietly disagree with them.
 *
 * ## The two stores, and why there are two
 *
 * - **The URL** carries what must survive a refresh. It is lossy on purpose: `more` is not a place,
 *   a half-finished sentence move is not one either, and neither is written.
 * - **`history.state`** carries the exact snapshot for that entry, plus which overlays were open.
 *   It does not survive being pasted into another tab, and does not need to.
 *
 * So a pop **prefers the snapshot and falls back to parsing the URL**. The fallback is not
 * theoretical: an entry pushed before this code shipped, a hand-edited address bar, and a restored
 * session all arrive with `history.state` null, and none of them may throw.
 *
 * ## Overlays
 *
 * A lightbox or the radical picker is a layer, not a location: it gets an entry so Back closes it,
 * but the URL does not change and a refresh does not restore it. Two rules keep the stack honest:
 *
 * - **Closing one by any other means consumes its entry** (`history.back()` from the closer), or the
 *   stack fills with entries whose overlay is already shut and Back appears to do nothing.
 * - **Forward never re-opens one.** A lightbox has a filename the history stack never knew, so an
 *   entry it left behind is inert on the way forward. This is why anything with an implicit teardown
 *   — the added-card panel, popovers, renames — is on the churn list instead of being an overlay.
 */

import type { DestinationId } from "./destinations";
import {
  DEFAULT_NAV_STATE,
  parseNavUrl,
  type CardsUrlState,
  type GrammarUrlState,
  type GroupsUrlState,
  type NavState,
  type ReadUrlState,
  type ReviewUrlState,
  type SearchUrlState,
  type SetupUrlState,
  type StatsUrlState,
} from "./navUrl";

/**
 * The layers Back can close.
 *
 * Deliberately short. A candidate belongs here only if it takes over a whole region, has no other
 * way to be dismissed than an explicit close, and is worth a press of Back on its own — which rules
 * out the sentence `⋯` menu, the tab overflow, in-place renames and the added-card panel. Those are
 * Escape's business and touch history not at all.
 *
 * `dialog` joined in 日本-317 and meets all three plainly: it takes the whole screen, it blocks,
 * and it cannot be dismissed except by answering it. Back means **Cancel** — the same answer
 * Escape and the scrim give, so a caller cannot tell them apart and cannot get it wrong. The
 * `⋯`-menu exclusion above is unchanged and is now load-bearing in the other direction too: the
 * shared menu 日本-318 introduced is provider-owned and still writes no history, because it has an
 * implicit teardown and is not worth a press of Back.
 */
export type OverlayId = "lightbox" | "picker" | "dialog" | "more" | "photo";

/**
 * `photo` — Read's photo capture on a phone (日本-711) — is the fifth, on `more`'s three tests: it
 * covers the whole screen, nothing behind it is reachable, and Back means *put the photo away*.
 * Deliberately not a URL param either: a half-set corner is not a place, and a reload landing on a
 * reopened camera flow with no photo in it would be a page of nothing.
 */

/**
 * `more` — the phone's その他 sheet (日本-497) — is the fourth, and it meets the same three tests:
 * it covers the surface, it is the only thing you can interact with while it is up, and Back
 * plainly means *close it* rather than *leave the page behind it*.
 *
 * **It is a layer and NOT a tab, and that distinction is the entire point.** The sheet this
 * replaces was `tab: "more"` — a tab that was not a place, which `destination()` threw on and
 * `lastRealTab` existed to paper over. As an overlay it writes a history entry and no URL, so
 * `NavState.tab` never stops naming a real destination and a reload lands on the surface rather
 * than on a reopened sheet.
 */

/** What is written into `history.state` for every entry this app creates. */
export interface EntryState {
  /** Monotonic. Tells a Back from a Forward, and a one-step pop from a jump down the stack. */
  idx: number;
  nav: NavState;
  /** Open when the entry was created, bottom to top. */
  overlays: readonly OverlayId[];
}

export type NavPatch = {
  tab?: DestinationId;
  cards?: Partial<CardsUrlState>;
  read?: Partial<ReadUrlState>;
  review?: Partial<ReviewUrlState>;
  search?: Partial<SearchUrlState>;
  grammar?: Partial<GrammarUrlState>;
  setup?: Partial<SetupUrlState>;
  stats?: Partial<StatsUrlState>;
  groups?: Partial<GroupsUrlState>;
};

/**
 * Apply a patch.
 *
 * Shallow per surface, which is what every call site wants: `{cards: {note: 5}}` must not clear the
 * deck, and `{read: {view: "library"}}` must not forget which log you were in.
 */
export function applyPatch(state: NavState, patch: NavPatch): NavState {
  return {
    tab: patch.tab ?? state.tab,
    cards: patch.cards ? { ...state.cards, ...patch.cards } : state.cards,
    read: patch.read ? { ...state.read, ...patch.read } : state.read,
    review: patch.review ? { ...state.review, ...patch.review } : state.review,
    search: patch.search ? { ...state.search, ...patch.search } : state.search,
    grammar: patch.grammar ? { ...state.grammar, ...patch.grammar } : state.grammar,
    setup: patch.setup ? { ...state.setup, ...patch.setup } : state.setup,
    stats: patch.stats ? { ...state.stats, ...patch.stats } : state.stats,
    groups: patch.groups ? { ...state.groups, ...patch.groups } : state.groups,
  };
}

export type NavEffect = "push" | "replace" | "none";

/**
 * Does this transition deserve a history entry?
 *
 * **This is the policy table, and it is the default for every call site** — a navigation helper that
 * asks this function cannot be got wrong by a surface added later, which a convention like "remember
 * to push here" would be. The two known exceptions (closing a reading tab, and any deliberate
 * amend) pass `replace` explicitly at the call site.
 *
 * What is *absent* matters as much as what is here. Sentence focus, `j`/`k` through the dock's
 * results, the added-card panel, hover selection, in-place renames, popovers, the bulk-selection
 * bar and the phone sheet's position are all churn: they are not in `NavState` at all, so they
 * cannot reach history even by accident.
 */
export function classify(prev: NavState, next: NavState): NavEffect {
  if (prev === next) return "none";

  // A place you can be. Each of these is somewhere Back should return you to.
  if (prev.tab !== next.tab) return "push";
  if (prev.read.view !== next.read.view) return "push";
  if (prev.read.log !== next.read.log) return "push";
  if (prev.cards.note !== next.cards.note) return "push";
  // Starting a sitting is a place; ending one is not, and that asymmetry is why `Review` passes
  // `{replace: true}` on the way out. Back from a session returns to the landing screen; Back from
  // the landing must not walk into a sitting that has been ended.
  if (prev.review.deck !== next.review.deck) return "push";
  // Opening a grammar entry is a PLACE (日本-519): it is what Read's 開く deep-links to and what a
  // reader shares, so Back closes it. Its query and two filters are refinements and fall through to
  // the replace below, on `cards.find`'s reasoning.
  if (prev.grammar.entry !== next.grammar.entry) return "push";
  if (prev.setup.jobId !== next.setup.jobId) return "push";
  if (prev.setup.historyOpen !== next.setup.historyOpen) return "push";
  if (prev.setup.deckId !== next.setup.deckId) return "push";
  // A Stats tab is a destination in its own right — History and Pace answer different questions and
  // Back should return you to the one you came from. The deck beside it is not: it refines the
  // population of the tab you are already on, and falls through to `replace` below.
  if (prev.stats.tab !== next.stats.tab) return "push";
  // 混同 (日本-601): an open group is a place, a drill is a sitting Back leaves, and the group
  // page's dock on a note is a place for the same reason 単語's is.
  if (prev.groups.group !== next.groups.group) return "push";
  if (!sameDrill(prev.groups.drill, next.groups.drill)) return "push";
  if (prev.groups.cards.note !== next.groups.cards.note) return "push";

  // A refinement of where you already are: the filter chips, the free-text field, the deck
  // segmented control, and the sentence-move intent. Worth surviving a refresh, not worth a press
  // of Back each.
  return sameState(prev, next) ? "none" : "replace";
}

function sameState(a: NavState, b: NavState): boolean {
  return (
    a.tab === b.tab &&
    a.read.view === b.read.view &&
    a.read.log === b.read.log &&
    a.read.moving === b.read.moving &&
    a.review.deck === b.review.deck &&
    // Compared here and deliberately absent from `classify`'s push list above: typing is a
    // refinement of where you already are, so it replaces. Leaving it out of THIS function is the
    // failure that looks like the feature working — every keystroke would compare equal, `classify`
    // would answer `"none"`, and the query would never reach the URL at all.
    a.search.q === b.search.q &&
    // Same trap as `search.q` directly above, three times over: these replace rather than push, so
    // they are absent from `classify` — and leaving them out of HERE too would make every keystroke
    // and every filter press compare equal, so the URL would never move at all.
    a.grammar.q === b.grammar.q &&
    a.grammar.volume === b.grammar.volume &&
    a.grammar.partOfSpeech === b.grammar.partOfSpeech &&
    a.grammar.entry === b.grammar.entry &&
    a.setup.jobId === b.setup.jobId &&
    a.setup.historyOpen === b.setup.historyOpen &&
    a.setup.deckId === b.setup.deckId &&
    a.stats.tab === b.stats.tab &&
    a.stats.deck === b.stats.deck &&
    sameCards(a.cards, b.cards) &&
    a.groups.group === b.groups.group &&
    sameDrill(a.groups.drill, b.groups.drill) &&
    sameCards(a.groups.cards, b.groups.cards)
  );
}

function sameDrill(a: readonly string[] | null, b: readonly string[] | null): boolean {
  if (a === null || b === null) return a === b;
  return sameList(a, b);
}

function sameCards(a: CardsUrlState, b: CardsUrlState): boolean {
  return (
    a.deck === b.deck &&
    a.text === b.text &&
    a.note === b.note &&
    sameList(a.states, b.states) &&
    sameList(a.flags, b.flags) &&
    sameList(a.tags, b.tags)
  );
}

function sameList<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Read an `EntryState` back off a `PopStateEvent`, tolerating everything that is not one.
 *
 * `null` for an entry pushed by another app on the same origin, by a build of this one from before
 * this code shipped, or by the browser restoring a session. The caller falls back to the URL.
 */
export function readEntryState(raw: unknown): EntryState | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Partial<EntryState>;
  if (typeof value.idx !== "number" || !value.nav || typeof value.nav !== "object") return null;
  return {
    idx: value.idx,
    // Merged over the defaults so an entry written by an older shape cannot leave a field undefined
    // and take a surface down on the way back.
    nav: { ...DEFAULT_NAV_STATE, ...value.nav },
    overlays: Array.isArray(value.overlays) ? (value.overlays as OverlayId[]) : [],
  };
}

export interface PopResolution {
  /** Close these, **top first**. Overlays open now that the entry we landed on did not have. */
  overlaysToClose: readonly OverlayId[];
  /** What is open after the pop. Forward into an overlay entry re-opens nothing, by design. */
  overlays: readonly OverlayId[];
  /** The state to commit. */
  nav: NavState;
  idx: number;
}

/**
 * Turn a `popstate` into "close these, then be here".
 *
 * Pure, so the awkward cases are testable without a browser: a jump several entries down the stack
 * closes *every* overlay it passed, an entry with no state falls back to the URL, and a forward step
 * into an entry that once had an overlay open leaves it shut.
 */
export function resolvePop(
  current: { overlays: readonly OverlayId[] },
  raw: unknown,
  search: string,
): PopResolution {
  const target = readEntryState(raw) ?? {
    idx: -1,
    nav: parseNavUrl(search),
    overlays: [] as readonly OverlayId[],
  };

  const overlays = current.overlays.filter((id) => target.overlays.includes(id));
  const overlaysToClose = current.overlays.filter((id) => !target.overlays.includes(id)).reverse();

  return { overlaysToClose, overlays, nav: target.nav, idx: target.idx };
}

/** Push an overlay onto the stack. */
export function pushOverlay(
  overlays: readonly OverlayId[],
  id: OverlayId,
): readonly OverlayId[] {
  return overlays.includes(id) ? overlays : [...overlays, id];
}

/** Take one off, wherever it sits — a closer fires for its own overlay, not for the top one. */
export function popOverlay(
  overlays: readonly OverlayId[],
  id: OverlayId,
): readonly OverlayId[] {
  return overlays.filter((open) => open !== id);
}
